import React from "react";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { PiArrowRight, PiUserFill } from "react-icons/pi";

import client from "../../client";
import { useOverlay } from "../../providers";
import { Bill } from "../../models";
import { Card, FintechAppButtons, IconButton, PaymentPanel, PlateNumberPanel } from "../../components";
import { QRCodeCanvas } from "qrcode.react";
import { FaCircleInfo, FaCopy } from "react-icons/fa6";
import { Check } from "../../assets";

const AUTO_CHECK_INTERVAL_MS = 5000;
const AUTO_CHECK_DURATION_MS = 5 * 60 * 1000;

const PaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { invoiceIds } = useParams();
  const invoiceIdsToUse = invoiceIds?.split(",") ?? [];
  const { setLoading, addToast, clearToast, addDialog, removeLastDialog } = useOverlay();
  const [isIndividual, setIsIndividual] = React.useState(true);
  const [isEntityRN, setIsEntityRN] = React.useState(true);
  const [entityRN, setEntityRN] = React.useState("");
  const [entityRNisSet, setEntityRNIsSet] = React.useState(false);
  const [orgName, setOrgName] = React.useState("");

  const [billList, setBillList] = React.useState<Bill[]>([]);
  const [isAutoCheckingPayment, setIsAutoCheckingPayment] = React.useState(true);
  const [warningDialogShown, setWarningDialogShown] = React.useState(false);

  const getBillDetail = React.useCallback(() => {
    if (invoiceIdsToUse.length < 1) {
      navigate(-1);
      return;
    }

    setLoading(true);

    const billDetailRequests = invoiceIdsToUse.map((invoiceId) =>
      client
        .post("/v1.0/getInvoiceDetail", { invoiceId })
        .then((response) => {
          const billParsed = Bill.fromJson(response.data);
          if (billParsed.invoiceId !== Number(invoiceId)) {
            navigate("/");
            clearToast();
            addToast({ variant: "danger" });
          }
          setBillList((prev) => {
            if (prev.every((e) => e.invoiceId !== billParsed.invoiceId)) {
              return [...prev, billParsed];
            }
            return prev;
          });
        })
        .catch((error) => {
          navigate(-1);
          clearToast();
          addToast({ variant: "danger", description: error });
        }),
    );

    Promise.allSettled(billDetailRequests).finally(() => {
      setLoading(false);
    });
  }, []);

  React.useEffect(() => {
    getBillDetail();
  }, [getBillDetail]);

  React.useEffect(() => {
    if (billList.length < 1 || warningDialogShown) return;

    addDialog({
      width: 320,
      body: (
        <div className="p-8 space-y-2 flex-center flex-col">
          <PlateNumberPanel plateNumber={billList[0].carStateNum} />
          <div className="text-3xl text-center py-2">{t("payment.warningMessage")}</div>
          <button
            type="button"
            className="mt-1 w-full text-lg font-bold bg-primary hover:bg-primary/80 text-primary-foreground py-3 px-6 rounded-xl"
            onClick={() => {
              removeLastDialog();
            }}
          >
            {t("common.correct")}
          </button>
          <button
            type="button"
            className="mt-1 w-full text-lg font-bold bg-secondary hover:bg-secondary/80 text-secondary-foreground py-3 px-6 rounded-xl"
            onClick={() => {
              removeLastDialog();
              navigate(`/`);
            }}
          >
            {t("common.retry")}
          </button>
        </div>
      ),
    });

    setWarningDialogShown(true);
  }, [addDialog, billList.length, navigate, removeLastDialog, t, warningDialogShown]);

  const handleEntityRNChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    let trimmed: string;

    if (isEntityRN) {
      // Entity RN: 7 digits
      const digitsOnly = e.target.value.replace(/\D/g, "");
      trimmed = digitsOnly.slice(0, 7);
    } else {
      // Individual RN: 2 Cyrillic + 8 digits
      const value = e.target.value.toUpperCase();
      // Extract letters and digits separately
      const letters = value.replace(/[^А-ЯӨҮЁ]/g, "").slice(0, 2);
      const digits = value.replace(/[^0-9]/g, "").slice(0, 8);
      trimmed = letters + digits;
    }

    setEntityRN(trimmed);
    // Reset orgName and entityRNisSet if entityRN changes after being successfully set
    if (entityRNisSet && trimmed !== entityRN) {
      setEntityRNIsSet(false);
      setOrgName("");
    }
  };

  const setVatInfo: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    const requiredLength = isEntityRN ? 7 : 10;

    if (entityRN.length === requiredLength && invoiceIdsToUse[0]) {
      const body = {
        invoiceId: Number(invoiceIdsToUse[0]),
        orgRegNo: entityRN,
      };

      setLoading(true);
      client
        .post("/v1.0/setInvoiceVatInfo", body)
        .then((res) => {
          if (res.data["responseCode"] === 0) {
            setEntityRNIsSet(true);
            setOrgName(res.data["orgName"] || "");
          }
        })
        .catch((error) => {
          clearToast();
          addToast({ variant: "danger", description: error });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const ebarimtDialog = React.useCallback(
    (data: any) => (
      <div className="flex-center flex-col gap-4 px-20 py-8 max-sm:px-4 max-sm:py-4">
        <div className="bg-blue-100 border flex items-start rounded-xl p-3 gap-3">
          <div className="rounded-full p-1 bg-blue-500">
            <FaCircleInfo className="text-white" />
          </div>
          <div className="text-2xl">{t("ebarimt.reminder")}</div>
        </div>
        <div className="flex-center gap-2">
          <img src={Check} className="w-8" />
          <div className="text-primary text-2xl font-medium">{t("ebarimt.paymentSuccessful")}</div>
        </div>
        <div className="w-full space-y-2">
          <div className="text-start text-2xl font-medium">{t("ebarimt.title")}</div>
          <div className="text-start">{t("ebarimt.message")}</div>
        </div>
        <QRCodeCanvas value={data.vatQrData} size={256} bgColor="white" fgColor="black" level="M" marginSize={1} />
        <div className="w-full flex-between gap-2">
          <div className="w-full space-y-2">
            <div className="text-start">{t("ebarimt.amountToRegister")}</div>
            <div className="text-start text-xl font-bold">
              {Intl.NumberFormat("mn-MN", {
                style: "currency",
                currency: "MNT",
                currencySign: "standard",
                currencyDisplay: "symbol",
              }).format(data.totalPayAmt ?? 0)}
            </div>
          </div>
          <div className="w-full space-y-2">
            <div className="text-end">{t("ebarimt.lotteryNumber")}</div>
            <div className="text-end text-xl font-bold">
              {data.vatLotteryNo}
              <button
                className="p-1"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(data.vatLotteryNo);
                    addToast({ variant: "success" });
                  } catch (e) {
                    addToast({ variant: "danger", description: e });
                  }
                }}
              >
                <FaCopy />
              </button>
            </div>
          </div>
        </div>
        <button
          type="button"
          className="mt-1 w-full text-lg font-bold bg-primary hover:bg-primary/80 text-primary-foreground py-3 px-6 rounded-xl"
          onClick={() => {
            removeLastDialog();
            navigate("/");
          }}
        >
          {t("ebarimt.finish")}
        </button>
      </div>
    ),
    [addToast, billList, navigate, removeLastDialog, t],
  );

  const checkPayment = React.useCallback(
    ({ showLoader = true }: { showLoader?: boolean } = {}) => {
      if (billList.length < 1) {
        return;
      }

      if (showLoader) {
        setLoading(true);
      }
      client
        .post("/v1.0/checkPayment", { invoiceId: billList[0].invoiceId })
        .then((res) => {
          clearToast();
          if (res.data.paymentStatus === "PAID") {
            addToast({ variant: "success", description: t("ebarimt.paymentSuccessful") });
            addDialog({ body: ebarimtDialog(res.data), width: "40rem", onDismiss: () => navigate("/") });
            setIsAutoCheckingPayment(false);
          } else {
            if (showLoader) {
              addToast({ variant: "warning", description: t("payment.waitingPayment") });
            }
          }
        })
        .catch((error) => {
          clearToast();
          addToast({ variant: "danger", description: error });
        })
        .finally(() => {
          if (showLoader) {
            setLoading(false);
          }
        });
    },
    [billList],
  );

  React.useEffect(() => {
    // Only auto-check when: isIndividual (individual) OR entityRNisSet (entity with RN set)
    const shouldAutoCheck = isIndividual || entityRNisSet;

    if (!shouldAutoCheck || billList.length < 1) {
      return;
    }

    checkPayment({ showLoader: false });
    const intervalId = setInterval(() => checkPayment({ showLoader: false }), AUTO_CHECK_INTERVAL_MS);
    const timeoutId = setTimeout(() => {
      setIsAutoCheckingPayment(false);
    }, AUTO_CHECK_DURATION_MS);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [checkPayment, isAutoCheckingPayment, billList.length, isIndividual, entityRNisSet]);

  if (billList == null || billList.length < 1) return null;

  return (
    <div className="p-8 flex max-lg:flex-col gap-8">
      <div className="lg:flex-1">
        <PaymentPanel bills={billList} />
      </div>
      <div className="lg:flex-1 space-y-4">
        <Card header={<div className="text-start w-full">{t("payment.vatType")}</div>}>
          <div className="gap-4 flex-center w-full">
            <div
              className="bg-card p-4 shadow flex-center gap-2 text-primary border rounded-xl max-md:flex-1 w-full"
              style={{ borderColor: isIndividual && "var(--primary) !important" }}
              onClick={() => setIsIndividual(true)}
            >
              <input type="radio" checked={isIndividual} className="accent-primary" />
              <div className="text-center">{t("payment.vatIndividual")}</div>
              <PiUserFill />
            </div>
            <div
              className="bg-card p-4 shadow flex-center gap-2 text-primary border rounded-xl max-md:flex-1 w-full"
              style={{ borderColor: !isIndividual && "var(--primary) !important" }}
              onClick={() => setIsIndividual(false)}
            >
              <input type="radio" checked={!isIndividual} className="accent-primary" />
              <div className="text-center">{t("payment.vatEntity")}</div>
              <PiUserFill />
            </div>
          </div>
          {!isIndividual && (
            <div className="flex-center mt-4">
              <div className="lg:max-w-full w-full">
                <div className="flex gap-2">
                  <div
                    onClick={() => {
                      setIsEntityRN(true);
                      setEntityRNIsSet(false);
                      setOrgName("");
                      setEntityRN("");
                    }}
                    className={`${isEntityRN ? "bg-primary text-primary-foreground" : "bg-background text-background-foreground"} rounded-t-xl px-4 py-1.5 font-bold flex-1`}
                  >
                    {t("payment.vatEntityRN")}
                  </div>
                  <div
                    onClick={() => {
                      setIsEntityRN(false);
                      setEntityRNIsSet(false);
                      setOrgName("");
                      setEntityRN("");
                    }}
                    className={`${isEntityRN ? "bg-background text-background-foreground" : "bg-primary text-primary-foreground"} rounded-t-xl px-4 py-1.5 font-bold flex-1`}
                  >
                    {t("payment.vatIndividualRN")}
                  </div>
                </div>
                <form
                  className="bg-card flex-center rounded-xl border pr-3 gap-2 mt-1.5"
                  style={{ borderColor: "var(--primary) !important" }}
                  onSubmit={setVatInfo}
                >
                  <input
                    className="w-full text-primary tracking-[1rem] px-5 py-2.5 text-xl rounded-b-xl focus-visible:outline-none font-bold"
                    placeholder={isEntityRN ? "0000000" : "АА00000000"}
                    value={entityRN}
                    onChange={handleEntityRNChange}
                    required
                    minLength={isEntityRN ? 7 : 10}
                    maxLength={isEntityRN ? 7 : 10}
                    autoFocus
                  />
                  <IconButton type="submit" icon={PiArrowRight} size="sm" />
                </form>
                {entityRNisSet && orgName && (
                  <div className="flex-between text-lg font-semibold pt-4">
                    <div>{t("payment.orgName")}</div>
                    <div>{orgName}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </Card>
        {((!isIndividual && entityRNisSet) || isIndividual) && (
          <>
            <Card header={<div>{t("payment.fintechApps.title")}</div>}>
              <FintechAppButtons qrCode={billList[0].qrCode} />
            </Card>
            <Card header={t("payment.qrCode")}>
              <div className="flex-center">
                <QRCodeCanvas
                  value={billList[0].qrCode}
                  size={256}
                  bgColor="white"
                  fgColor="black"
                  level="M"
                  marginSize={1}
                />
              </div>
            </Card>
            <div className="flex-center">
              {isAutoCheckingPayment && (isIndividual || entityRNisSet) ? (
                <div className="text-center text-sm text-muted">{t("payment.waitingPayment")}</div>
              ) : (
                <button
                  type="button"
                  className="mt-1 w-full md:w-md text-2xl font-bold bg-primary hover:bg-primary/80 text-primary-foreground py-3 px-6 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => checkPayment({ showLoader: true })}
                  disabled={!isIndividual && !entityRNisSet}
                >
                  {t("payment.checkPayment")}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentPage;
